import type { IAiPromptApplyConfigItem } from '@/pages/datalab/services/ai/api';
import { CUSTOM_APPROACH_VALUE } from '../AiSettings/editorDraft';
import type { IAiAnalysisScope, IAiPromptInfo, IAiPromptOption } from '../types';

/**
 * 将 apply/get 返回的 chart_ids 映射为卡片运行时使用的 analysisScope。
 * - 空数组或与全量图表一致时视为「整个报表」；
 * - 否则为部分图表选择，并过滤掉布局中已不存在的 chartId。
 */
export const buildAnalysisScopeFromChartIds = (
  chartIds: string[] | undefined,
  allTaskIds: string[],
): IAiAnalysisScope => {
  const validSet = new Set(allTaskIds);
  const validChartIds = (chartIds || []).filter((chartId) => validSet.has(chartId));
  const isAllChartsSelected = allTaskIds.length > 0
    && validChartIds.length === allTaskIds.length;
  if (!validChartIds.length || isAllChartsSelected) {
    return {
      type: 'all',
      taskIds: allTaskIds,
    };
  }
  return {
    type: 'chart',
    taskIds: validChartIds,
  };
};

/**
 * 按 apply/save 写入的 name 在预置列表中查找对应项。
 * 保存侧口径：selectedOption.info.templateName || selectedOption.text（与 handleAutoApplyNextTime 一致）。
 */
export const findPresetOptionByApplyConfigName = (
  configName: string | undefined,
  promptOptions: IAiPromptOption[],
): IAiPromptOption | undefined => {
  const normalizedName = String(configName ?? '').trim();
  if (!normalizedName) {
    return undefined;
  }
  return promptOptions.find((option) => {
    if (String(option.value) === CUSTOM_APPROACH_VALUE) {
      return false;
    }
    const templateName = String(option.info?.templateName ?? '').trim();
    const displayText = String(option.text ?? '').trim();
    return normalizedName === templateName || normalizedName === displayText;
  });
};

/** 将后端 apply 配置转为卡片 promptInfo；能匹配预置 name 时保留预置选中态，否则按自定义思路 */
export const buildPromptInfoFromApplyConfig = (
  config: IAiPromptApplyConfigItem,
  allTaskIds: string[],
  promptOptions: IAiPromptOption[] = [],
): IAiPromptInfo => {
  const savedPromptContent = String(config.prompt_content ?? '');
  const savedAnalysisContext = String(config.analysis_context ?? '');
  const analysisScope = buildAnalysisScopeFromChartIds(config.chart_ids, allTaskIds);
  const matchedPreset = findPresetOptionByApplyConfigName(config.name, promptOptions);
  const numericPromptId = Number(matchedPreset?.value);
  if (matchedPreset && Number.isFinite(numericPromptId) && numericPromptId > 0) {
    return {
      analysisScope,
      promptId: numericPromptId,
      isCustom: false,
      customPrompt: {
        promptContent: savedPromptContent,
        analysisContext: savedAnalysisContext,
      },
    };
  }
  return {
    analysisScope,
    isCustom: true,
    customPrompt: {
      promptContent: savedPromptContent,
      analysisContext: savedAnalysisContext,
    },
  };
};

/** 保存 apply 配置时使用的 chart_ids：全报表范围传全量 taskIds，与读取侧口径一致 */
export const buildChartIdsForApplySave = (
  analysisScope: IAiAnalysisScope,
  allTaskIds: string[],
): string[] => {
  if (analysisScope.type === 'all') {
    return allTaskIds;
  }
  return analysisScope.taskIds;
};

export interface IResolveAiCardPromptInfoParams {
  isEdit: boolean;
  layoutPromptInfo?: IAiPromptInfo;
  applyPromptInfo?: IAiPromptInfo;
  /** 预览态 apply/get 请求中：暂不回落 layout，避免先用布局配置触发自动分析 */
  isApplyConfigLoading?: boolean;
}

/**
 * 合并卡片初始 promptInfo 的来源优先级。
 * - 预览态：自动应用配置 > 布局 promptInfo；
 * - 编辑态：布局 promptInfo > 自动应用配置（与预览相反，编辑以布局持久化为准）。
 */
export const resolveAiCardPromptInfo = (
  params: IResolveAiCardPromptInfoParams,
): IAiPromptInfo | undefined => {
  const {
    isEdit,
    layoutPromptInfo,
    applyPromptInfo,
    isApplyConfigLoading = false,
  } = params;
  if (!isEdit) {
    if (applyPromptInfo) {
      return applyPromptInfo;
    }
    if (isApplyConfigLoading) {
      return undefined;
    }
    return layoutPromptInfo;
  }
  return layoutPromptInfo ?? applyPromptInfo;
};

/** 兼容 datalabWithTokenHttp 不同 unwrap 形态的 apply/get 响应 */
export const normalizeApplyConfigItems = (
  response: unknown,
): IAiPromptApplyConfigItem[] => {
  const payload = response as
    | IAiPromptApplyConfigItem[]
    | { data?: IAiPromptApplyConfigItem[]; list?: IAiPromptApplyConfigItem[] };
  if (Array.isArray(payload)) {
    return payload;
  }
  if (Array.isArray(payload?.data)) {
    return payload.data;
  }
  if (Array.isArray(payload?.list)) {
    return payload.list;
  }
  return [];
};
